import { Alert, Anchor, Paragraph, Text } from '@zillow/constellation-10';
import {
  APPLICATIONS_FAQ_URL,
  LANDLORD_SCREENING_FAQ_URL,
  RENTAL_APPLICATION_REPORTS_LAWS_URL,
} from '@zg-rentals/applications-utils';
import type {
  BackgroundReportAlertBase,
  BackgroundReportAlertProps,
  BackgroundReportAlertSource,
} from '../../../models/background-report-models';
import { first } from 'lodash-es';
import { CicBackgroundReportStatus } from '@zg-rentals/ts-capps-client';
import type { BackgroundReport } from '@zg-rentals/ts-capps-client';
import { UserType } from '../../../models/report-models';

type BackgroundReportAlert = BackgroundReportAlertBase & {
  sources:
    | {
        backgroundReport: true;
      }
    | {
        creditReport: true;
      };
};

export function getBackgroundReportAlerts(
  userType: UserType,
  backgroundReport: Partial<BackgroundReport> | undefined,
): Array<BackgroundReportAlert> {
  const result: Array<BackgroundReportAlert> = [];
  if (backgroundReport == null) return result;

  const evictionReportStatus = backgroundReport.evictionReport?.reportStatus;
  const criminalReportStatus = backgroundReport.criminalReport?.reportStatus;

  if (
    evictionReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW ||
    criminalReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW
  ) {
    // When both are in MANUAL REVIEW:
    const both =
      criminalReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW &&
      evictionReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW;

    const alertText: Pick<BackgroundReportAlert, 'content' | 'summary'> = both
      ? {
          content:
            'Background report is under manual review due to regulatory requirements. The report should automatically refresh in 1 to 2 business days.',
          summary: 'Report unavailable',
        }
      : {
          content: `${criminalReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW ? 'Criminal Report' : 'Housing Court Record'} is under manual review due to regulatory requirements. The report should automatically refresh in 1 to 2 business days.`,
          summary: 'Report under manual review',
        };

    result.push({
      ...alertText,
      sources: {
        backgroundReport: true,
        evictionReport: evictionReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW,
        criminalReport: criminalReportStatus === CicBackgroundReportStatus.MANUAL_REVIEW,
      },
    });
  }

  if (evictionReportStatus === CicBackgroundReportStatus.REGULATORY_RESTRICTIONS) {
    result.push({
      summary: (
        <Text>
          Some records are unavailable due to{' '}
          <Anchor href={RENTAL_APPLICATION_REPORTS_LAWS_URL} target="_blank">
            local laws
          </Anchor>
        </Text>
      ),
      content: null,
      sources: {
        backgroundReport: true,
        evictionReport: true,
      },
    });
  }

  if (criminalReportStatus === CicBackgroundReportStatus.REGULATORY_RESTRICTIONS) {
    result.push({
      summary: (
        <Text>
          Some records are unavailable due to{' '}
          <Anchor href={RENTAL_APPLICATION_REPORTS_LAWS_URL} target="_blank">
            local laws
          </Anchor>
        </Text>
      ),
      content: null,
      sources: {
        backgroundReport: true,
        criminalReport: true,
      },
    });
  }

  if (
    evictionReportStatus === CicBackgroundReportStatus.SECURITY_FREEZE ||
    criminalReportStatus === CicBackgroundReportStatus.SECURITY_FREEZE
  ) {
    result.push({
      content: (
        <>
          <Paragraph>
            You have frozen your background check. You can still submit your application, but we recommend that you
            communicate the status of this report to the landlord, to ensure that your application is fully considered.
          </Paragraph>
          <br />
          <Paragraph>
            To unfreeze your background check, contact CIC customer assistance. Once unfrozen, the report will
            automatically become viewable within one business day.
          </Paragraph>
        </>
      ),
      summary: 'Report unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: criminalReportStatus === CicBackgroundReportStatus.SECURITY_FREEZE,
        evictionReport: evictionReportStatus === CicBackgroundReportStatus.SECURITY_FREEZE,
      },
    });
  }

  if (
    evictionReportStatus === CicBackgroundReportStatus.IN_DISPUTE &&
    criminalReportStatus === CicBackgroundReportStatus.IN_DISPUTE
  ) {
    result.push({
      content:
        'Background check can’t be pulled at this time. The report should automatically refresh with the complete information within 1 business day.',
      summary: 'Background check unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: true,
        evictionReport: true,
      },
    });
  }

  if (evictionReportStatus === CicBackgroundReportStatus.IN_DISPUTE) {
    result.push({
      content:
        'Housing Court Record can’t be pulled at this time. The record should automatically refresh with the complete information within 1 business day.',
      summary: 'Housing Court Record unavailable',
      sources: {
        backgroundReport: true,
        evictionReport: true,
      },
    });
  }

  if (criminalReportStatus === CicBackgroundReportStatus.IN_DISPUTE) {
    result.push({
      content:
        'Criminal Report can’t be pulled at this time. The report should automatically refresh with the complete information within 1 business day.',
      summary: 'Criminal Report unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: true,
      },
    });
  }

  if (
    [CicBackgroundReportStatus.INVALID_SSN, CicBackgroundReportStatus.INVALID_DOB].includes(
      criminalReportStatus as CicBackgroundReportStatus,
    )
  ) {
    result.push({
      content: (
        <Text>
          Some of the report information can’t be pulled due to an error. Please contact{' '}
          <Anchor
            href={userType === UserType.Landlord ? LANDLORD_SCREENING_FAQ_URL : APPLICATIONS_FAQ_URL}
            target="_blank"
          >
            customer support
          </Anchor>{' '}
          to report the issue.{' '}
          {userType === UserType.Renter && 'You can submit your application without the full report.'}
        </Text>
      ),
      summary: 'Criminal Report unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: true,
      },
    });
  }

  if (
    criminalReportStatus === CicBackgroundReportStatus.SERVICE_FAILED ||
    evictionReportStatus === CicBackgroundReportStatus.SERVICE_FAILED
  ) {
    result.push({
      content: (
        <Text>
          A report can’t be pulled due to an error. Please contact{' '}
          <Anchor
            href={userType === UserType.Landlord ? LANDLORD_SCREENING_FAQ_URL : APPLICATIONS_FAQ_URL}
            target="_blank"
          >
            customer support
          </Anchor>{' '}
          to report the issue.{' '}
          {userType === UserType.Renter && 'You can submit your application without the full report.'}
        </Text>
      ),

      summary: 'Report unavailable',
      sources: {
        backgroundReport: true,
        criminalReport: true,
        evictionReport: true,
      },
    });
  }

  return result;
}

export function BackgroundReportAlert({ userType, backgroundReport, onlySources = [] }: BackgroundReportAlertProps) {
  const alert = getFirstReportAlert(getBackgroundReportAlerts(userType, backgroundReport), onlySources)?.content;

  if (!alert) return null;

  return <Alert tone="warning">{alert}</Alert>;
}

/** Gets the first (the order is order of items in the generator functions)
 * report alert that has at least one alert source included in the "onlySources" array
 */
export function getFirstReportAlert(
  alerts: Array<BackgroundReportAlert>,
  onlySources: Array<BackgroundReportAlertSource>,
) {
  return first(alerts.filter((x) => isSourceIncludedInAlert(x.sources, onlySources)));
}

function isSourceIncludedInAlert(
  alertSources: Partial<Record<BackgroundReportAlertSource, boolean>>,
  onlySources: Array<BackgroundReportAlertSource>,
) {
  for (const source of onlySources) {
    if (!alertSources[source]) {
      // The alert source is only included in the "onlySources" array
      return false;
    }
  }
  return true;
}

/**
 * Returns the description of the report status if the status implies an
 * unavailable report. Returns null for AVAILABLE status.
 */
export function getReportStatusDescription(reportStatus: CicBackgroundReportStatus | null | undefined) {
  if (reportStatus === CicBackgroundReportStatus.AVAILABLE) {
    return null;
  }
  if (
    reportStatus == null ||
    [
      (CicBackgroundReportStatus.SERVICE_FAILED,
      CicBackgroundReportStatus.IN_DISPUTE,
      CicBackgroundReportStatus.MANUAL_REVIEW),
    ].includes(reportStatus)
  ) {
    return 'Pending';
  }
  if ([CicBackgroundReportStatus.INVALID_SSN, CicBackgroundReportStatus.INVALID_DOB].includes(reportStatus)) {
    return 'Error';
  }

  // Unidentified bug non-available status
  return 'Unavailable';
}

/** Returns true if one of these conditions is met:
 * - the report status is not AVAILABLE
 * - the report status is null
 */
export function isReportUnavailable(reportStatus: CicBackgroundReportStatus | null | undefined) {
  if (reportStatus === CicBackgroundReportStatus.REGULATORY_RESTRICTIONS) {
    // The report with REGULATORY_RESTRICTIONS status
    // is viewable but the modal shows an alert summary,
    // so it is not considered unavailable
    return false;
  }
  return getReportStatusDescription(reportStatus) != null;
}
